import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '..';
import type { DocumentationItem } from '@server/documentation';
import type { SalesforceItem } from '../../components/parser/ConfigurationItemTypes';
import type { SnowflakeConfigurationItem } from '@server/snowflake-documentation';
import type { Data360ConfigurationItem } from '@server/data360-documentation';
import type { HubspotConfigurationItem } from '@server/hubspot-documentation';
import type { WorkatoConfigurationItem } from '@server/workato-documentation';
import { DocumentationCardContentViewType } from '../../components/documentation/building-blocks/documentation-card/types';
import type { ConfigurationCommentDto } from '../../components/documentation/salesforce-documentation-card/annotations/comments/types';
import type {
  ReadOnlyAutomation,
  ReadOnlyDedupMatching,
  ReadOnlyRollup,
} from '@server/read-only-elements.types';

export interface PlatformItemMap {
  salesforce: SalesforceItem;
  snowflake: SnowflakeConfigurationItem;
  data360: Data360ConfigurationItem;
  hubspot: HubspotConfigurationItem;
  workato: WorkatoConfigurationItem;
}

export type DocumentationPlatform = keyof PlatformItemMap;

export type SelectedCardItem = {
  [P in DocumentationPlatform]: {
    platform: P;
    item: PlatformItemMap[P];
    contentType: DocumentationCardContentViewType;
  };
}[DocumentationPlatform];

export type CardRelationshipMap = Record<string, DocumentationItem[]>;

export interface DocumentationCardState {
  selectedItem: SelectedCardItem | null;
  history: SelectedCardItem[];

  dependsOn: Record<string, CardRelationshipMap>;
  usedBy: Record<string, CardRelationshipMap>;
  /** HubSpot: graph children (e.g. property ↔ property group); keyed by focal metadataId */
  related: Record<string, CardRelationshipMap>;
  sourceCodeByConfigurationId: {
    [crmOrgId: string]: { [configurationId: string]: string };
  };

  // ---- salesforce only data below ----
  commentsByMetadataId: {
    [crmOrgId: string]: { [metadataId: string]: ConfigurationCommentDto[] };
  };
  sweepElementsLatestDeployments: {
    [sweepElementId: string]: ReadOnlyAutomation | ReadOnlyDedupMatching | ReadOnlyRollup;
  };
}

const initialState: DocumentationCardState = {
  selectedItem: null,
  history: [],

  dependsOn: {},
  usedBy: {},
  related: {},
  commentsByMetadataId: {},
  sourceCodeByConfigurationId: {},
  sweepElementsLatestDeployments: {},
};

export const documentationCardSlice = createSlice({
  name: 'documentationCard',
  initialState,
  reducers: {
    setSelectedCardItem: (
      state,
      action: PayloadAction<SelectedCardItem & { clearHistory?: boolean }>,
    ) => {
      const { clearHistory, ...cardItem } = action.payload;
      if (clearHistory) {
        state.history = [];
      } else if (state.selectedItem) {
        state.history.push(state.selectedItem);
      }
      state.selectedItem = cardItem as SelectedCardItem;
    },

    goBackInCardHistory: (state) => {
      const previous = state.history.pop();
      state.selectedItem = previous ?? null;
    },

    closeCard: (state) => {
      Object.assign(state, initialState);
    },

    setCardContentType: (state, action: PayloadAction<DocumentationCardContentViewType>) => {
      if (state.selectedItem) {
        state.selectedItem.contentType = action.payload;
      }
    },

    setCardDependsOn: (
      state,
      action: PayloadAction<{ metadataId: string; items: CardRelationshipMap }>,
    ) => {
      state.dependsOn[action.payload.metadataId] = action.payload.items;
    },

    setCardUsedBy: (
      state,
      action: PayloadAction<{ metadataId: string; items: CardRelationshipMap }>,
    ) => {
      state.usedBy[action.payload.metadataId] = action.payload.items;
    },

    setCardRelated: (
      state,
      action: PayloadAction<{ metadataId: string; items: CardRelationshipMap }>,
    ) => {
      state.related[action.payload.metadataId] = action.payload.items;
    },

    addCommentForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        metadataId: string;
        newComment: ConfigurationCommentDto;
      }>,
    ) => {
      const { crmOrgId, metadataId, newComment } = action.payload;
      const oldComments = state.commentsByMetadataId[crmOrgId];
      const oldConfigurationComments = state.commentsByMetadataId[crmOrgId]?.[metadataId] ?? [];

      state.commentsByMetadataId[crmOrgId] = {
        ...oldComments,
        [metadataId]: [...oldConfigurationComments, newComment],
      };
    },

    updateCommentForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        metadataId: string;
        newComment: ConfigurationCommentDto;
      }>,
    ) => {
      const { crmOrgId, metadataId, newComment } = action.payload;
      const oldComments = state.commentsByMetadataId[crmOrgId];
      const oldConfigurationComments: ConfigurationCommentDto[] =
        state.commentsByMetadataId[crmOrgId]?.[metadataId] ?? [];

      state.commentsByMetadataId[crmOrgId] = {
        ...oldComments,
        [metadataId]: oldConfigurationComments.map((comment) =>
          comment.id === newComment.id ? newComment : comment,
        ),
      };
    },

    deleteCommentForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        metadataId: string;
        commentId: string;
      }>,
    ) => {
      const { crmOrgId, metadataId, commentId } = action.payload;
      const oldComments = state.commentsByMetadataId[crmOrgId];
      const oldConfigurationComments: ConfigurationCommentDto[] =
        state.commentsByMetadataId[crmOrgId]?.[metadataId] ?? [];

      state.commentsByMetadataId[crmOrgId] = {
        ...oldComments,
        [metadataId]: oldConfigurationComments.filter((comment) => comment.id !== commentId),
      };
    },

    setCommentsForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        metadataId: string;
        comments: ConfigurationCommentDto[];
      }>,
    ) => {
      const { crmOrgId, metadataId, comments } = action.payload;
      const oldComments = state.commentsByMetadataId[crmOrgId];
      state.commentsByMetadataId[crmOrgId] = {
        ...oldComments,
        [metadataId]: comments,
      };
    },

    setSourceCodeForConfiguration: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        configurationId: string;
        sourceCode: string;
      }>,
    ) => {
      const { crmOrgId, configurationId, sourceCode } = action.payload;
      const oldConfigs = state.sourceCodeByConfigurationId[crmOrgId];
      state.sourceCodeByConfigurationId[crmOrgId] = {
        ...oldConfigs,
        [configurationId]: sourceCode,
      };
    },

    setLatestDeployment: (
      state,
      action: PayloadAction<{
        elementId: string;
        readOnlyElement: ReadOnlyAutomation | ReadOnlyDedupMatching | ReadOnlyRollup;
      }>,
    ) => {
      const { readOnlyElement, elementId } = action.payload;
      state.sweepElementsLatestDeployments[elementId] = readOnlyElement;
    },
  },
});

export const {
  setSelectedCardItem,
  goBackInCardHistory,
  closeCard,
  setCardContentType,
  setCardDependsOn,
  setCardUsedBy,
  setCardRelated,
  addCommentForConfiguration,
  updateCommentForConfiguration,
  deleteCommentForConfiguration,
  setCommentsForConfiguration,
  setSourceCodeForConfiguration,
  setLatestDeployment,
} = documentationCardSlice.actions;

export const selectDocumentationCardItem = (state: RootState) =>
  state[documentationCardSlice.name]?.selectedItem ?? null;

export const selectDocumentationCardHistoryLength = (state: RootState) =>
  state[documentationCardSlice.name]?.history.length ?? 0;

export const selectDocumentationCardHistory = (state: RootState) =>
  state[documentationCardSlice.name]?.history ?? [];

export const selectCardContentType = (state: RootState) =>
  state[documentationCardSlice.name]?.selectedItem?.contentType ??
  DocumentationCardContentViewType.default;

export const selectCardDependsOn = (metadataId: string) => (state: RootState) =>
  state[documentationCardSlice.name]?.dependsOn[metadataId];

export const selectCardUsedBy = (metadataId: string) => (state: RootState) =>
  state[documentationCardSlice.name]?.usedBy[metadataId];

export const selectCardRelated = (metadataId: string) => (state: RootState) =>
  state[documentationCardSlice.name]?.related[metadataId];

export const selectConfigurationComments =
  (crmOrgId: string, metadataId: string) => (state: RootState) =>
    state[documentationCardSlice.name]?.commentsByMetadataId?.[crmOrgId]?.[metadataId];

export const selectConfigurationSourceCode =
  (crmOrgId: string, configurationId: string) => (state: RootState) =>
    state[documentationCardSlice.name]?.sourceCodeByConfigurationId?.[crmOrgId]?.[configurationId];

export const selectLatestDeployment = (elementId: string) => (state: RootState) =>
  state[documentationCardSlice.name]?.sweepElementsLatestDeployments[elementId];

export default documentationCardSlice.reducer;
